import React, { useState, useCallback, useMemo } from 'react';
import { Typography, Box, useMediaQuery, useTheme, Button, Grid } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { SelectField } from '../../../../../Components/Field';
import { TypedFormFieldValue } from '../../../../../Components/Form/Form.types';
import { StepProps } from './types';
import { StepButtonBar } from './StepButtonBar';
import OtherRequirementInfo from './OtherRequirementInfo';

const PRIMARY = '#c4122f';

interface DeleteStep1CompanyUnitProps extends StepProps {
  companyUnits: { id: number; name: string }[];
  onCompanyUnitSelect: (u: any) => void;
  currentStepName: string;
  otherRequirementInfo: any;
  setOtherRequirementInfo: (any) => void;
}

const DeleteStep1CompanyUnit = ({
  onNext,
  onBack,
  onCancel,
  currentData,
  isFirstStep,
  companyUnits,
  onCompanyUnitSelect,
  currentStepName,
  otherRequirementInfo,
  setOtherRequirementInfo,
}: DeleteStep1CompanyUnitProps) => {
  const [selectedUnit, setSelectedUnit] = useState<{ id: number; name: string } | null>(
    (currentData as any).CustomerCompanyUnitId != null
      ? companyUnits.find((u) => u.id === (currentData as any).CustomerCompanyUnitId) || null
      : null,
  );
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const paperPadding = isMobile ? theme.spacing(3) : theme.spacing(8);
  const selectOptions = useMemo(
    () => companyUnits.map((u) => ({ value: u.id, label: u.name })),
    [companyUnits],
  );

  const handleChange = useCallback(
    (val: TypedFormFieldValue<number> | undefined) => {
      if (val) {
        const unit = companyUnits.find((u) => u.id === val.value);
        setSelectedUnit(unit || null);
      } else {
        setSelectedUnit(null);
      }
    },
    [companyUnits],
  );

  const handleNext = () => {
    if (selectedUnit) {
      onCompanyUnitSelect(selectedUnit);
      onNext({ CustomerCompanyUnitId: selectedUnit.id, _companyUnitName: selectedUnit.name });
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          width: '100%',
          gap: '10px',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'stretch',
        }}
      >
        <div
          style={{
            width: isMobile ? '100%' : 'calc(50% - 5px)',
            flexShrink: 0,
            padding: paperPadding,
            backgroundColor: '#f7f0f1',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <Box style={{ maxWidth: '320px', width: '100%' }}>
            <Grid container spacing={3} justifyContent="flex-start">
              <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center' }}>
                <SelectField
                  name="company-unit"
                  placeholder={formatMessage({ id: 'form.selectCompanyUnits' })}
                  options={selectOptions}
                  errorTexts={[]}
                  initialState={
                    selectedUnit ? { value: selectedUnit.id, label: selectedUnit.name } : undefined
                  }
                  onChange={handleChange}
                />
              </Grid>
            </Grid>
          </Box>
        </div>

        <OtherRequirementInfo
          otherRequirementStepInfo={otherRequirementInfo}
          currentStepName={currentStepName}
          onUpdate={setOtherRequirementInfo}
        />
      </div>

      <Box>
        <StepButtonBar
          onCancel={onCancel}
          onBack={onBack}
          onNext={handleNext}
          nextLabel="form.next"
          nextDisabled={!selectedUnit}
          formatMessage={formatMessage}
          showBack={true}
        />
      </Box>
    </div>
  );
};

export default DeleteStep1CompanyUnit;
